import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Card from "../components/Card";

const reportTypes = [
  "Transaction Summary",
  "Profit & Loss",
  "Team Performance",
  "Withdrawal History",
  "Deposit History",
];

const defaultDateRange = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
};

const presetToRange = (preset) => {
  const end = new Date();
  const start = new Date();
  if (preset === "Last 7 Days") start.setDate(end.getDate() - 7);
  else if (preset === "Last 30 Days") start.setDate(end.getDate() - 30);
  else if (preset === "Last 90 Days") start.setDate(end.getDate() - 90);
  else return null;
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
};

const Reports = () => {
  const [searchParams] = useSearchParams();
  const initialRange = useMemo(() => defaultDateRange(), []);
  const [startDate, setStartDate] = useState(initialRange.start);
  const [endDate, setEndDate] = useState(initialRange.end);
  const [reportType, setReportType] = useState(reportTypes[0]);
  const [txTypeFilter, setTxTypeFilter] = useState("All");
  const [txStatusFilter, setTxStatusFilter] = useState("All");
  const [txSearch, setTxSearch] = useState("");

  useEffect(() => {
    const rt = searchParams.get("reportType");
    if (rt && reportTypes.includes(rt)) setReportType(rt);

    const preset = searchParams.get("txDatePreset");
    if (preset) {
      const r = presetToRange(preset);
      if (r) {
        setStartDate(r.start);
        setEndDate(r.end);
      }
    }

    const t = searchParams.get("txType");
    if (t) setTxTypeFilter(t);
    const st = searchParams.get("txStatus");
    if (st) setTxStatusFilter(st);
    const q = searchParams.get("txSearch");
    if (q) setTxSearch(q);
  }, [searchParams]);

  const recentReports = [
    {
      id: 1,
      type: "Transaction Summary",
      dateGenerated: "2023-10-26 14:30",
      status: "Completed",
    },
    {
      id: 2,
      type: "Profit & Loss",
      dateGenerated: "2023-10-25 10:00",
      status: "Completed",
    },
    {
      id: 3,
      type: "Team Performance",
      dateGenerated: "2023-10-24 16:45",
      status: "Processing",
    },
    {
      id: 4,
      type: "Withdrawal History",
      dateGenerated: "2023-10-23 09:15",
      status: "Completed",
    },
    {
      id: 5,
      type: "Deposit History",
      dateGenerated: "2023-10-22 11:00",
      status: "Completed",
    },
  ];

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const options = { year: "numeric", month: "long", day: "numeric" };
    return date.toLocaleDateString("en-US", options);
  };

  return (
    <div className="flex min-h-screen bg-[var(--bg-main)]">
      <Sidebar />
      <div className="flex-1 p-4 md:p-8 md:ml-64">
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-bold mb-2 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Reports
          </h1>
          <p className="text-[var(--text-primary)] text-sm md:text-base">
            View report settings. File export is disabled on the user dashboard.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          <Card>
            <h2 className="text-xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Select Date Range
            </h2>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  Start Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full px-4 py-3 pl-12 rounded-xl bg-[var(--bg-main)] border border-primary/20 text-[var(--text-primary)] focus:outline-none focus:border-primary focus:shadow-lg focus:shadow-[#22c55e]/20 transition-all"
                  />
                  <svg
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-primary)] opacity-70"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-xs text-[var(--text-primary)] opacity-60 mt-2">
                  {formatDate(startDate)}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                  End Date
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full px-4 py-3 pl-12 rounded-xl bg-[var(--bg-main)] border border-primary/20 text-[var(--text-primary)] focus:outline-none focus:border-primary focus:shadow-lg focus:shadow-[#22c55e]/20 transition-all"
                  />
                  <svg
                    className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[var(--text-primary)] opacity-70"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                </div>
                <p className="text-xs text-[var(--text-primary)] opacity-60 mt-2">
                  {formatDate(endDate)}
                </p>
              </div>
            </div>
          </Card>

          <Card>
            <h2 className="text-xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
              Report type
            </h2>

            <div className="mb-6">
              <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                Report Type
              </label>
              <select
                value={reportType}
                onChange={(e) => setReportType(e.target.value)}
                className="w-full px-4 py-3 rounded-xl bg-[var(--bg-main)] border border-primary/20 text-[var(--text-primary)] focus:outline-none focus:border-primary focus:shadow-lg focus:shadow-[#22c55e]/20 transition-all"
              >
                {reportTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {(reportType === "Transaction Summary" || reportType === "Deposit History") && (
              <div className="space-y-3 mb-6">
                <div>
                  <label className="block text-xs font-medium text-[var(--text-primary)] mb-1">
                    Transaction type (optional)
                  </label>
                  <select
                    value={txTypeFilter}
                    onChange={(e) => setTxTypeFilter(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-[var(--bg-main)] border border-primary/20 text-[var(--text-primary)] text-sm"
                  >
                    {["All", "Deposit", "Withdrawal", "Arbitrage Trade", "Gold Trade", "Bonus"].map(
                      (t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ),
                    )}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-primary)] mb-1">
                    Status (optional)
                  </label>
                  <select
                    value={txStatusFilter}
                    onChange={(e) => setTxStatusFilter(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-[var(--bg-main)] border border-primary/20 text-[var(--text-primary)] text-sm"
                  >
                    {["All", "Completed", "Pending", "Failed"].map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--text-primary)] mb-1">
                    Search (optional)
                  </label>
                  <input
                    value={txSearch}
                    onChange={(e) => setTxSearch(e.target.value)}
                    placeholder="ID, type, network…"
                    className="w-full px-3 py-2 rounded-lg bg-[var(--bg-main)] border border-primary/20 text-[var(--text-primary)] text-sm"
                  />
                </div>
              </div>
            )}

            <p className="text-sm text-[var(--text-primary)] opacity-70">
              CSV and PDF export are not available here. Use Transactions and
              Wallet pages for on-screen history.
            </p>
          </Card>
        </div>

        <Card className="mb-8">
          <h2 className="text-xl md:text-2xl font-bold mb-6 bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent">
            Recently Generated Reports
          </h2>

          <div className="hidden md:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-primary/20">
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Report Type
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Date Generated
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Status
                  </th>
                  <th className="text-left py-4 px-4 text-[var(--text-primary)] text-sm font-medium">
                    Export
                  </th>
                </tr>
              </thead>
              <tbody>
                {recentReports.map((report) => (
                  <tr
                    key={report.id}
                    className="border-b border-primary/10 last:border-0 hover:bg-[#22c55e]/5 transition-colors"
                  >
                    <td className="py-4 px-4 text-[var(--text-primary)] font-medium">
                      {report.type}
                    </td>
                    <td className="py-4 px-4 text-[var(--text-primary)]">
                      {report.dateGenerated}
                    </td>
                    <td className="py-4 px-4">
                      <span
                        className={`text-sm font-semibold ${
                          report.status === "Completed"
                            ? "bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent"
                            : "text-yellow-400"
                        }`}
                      >
                        {report.status}
                      </span>
                    </td>
                    <td className="py-4 px-4 text-[var(--text-primary)] opacity-50 text-sm">
                      —
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="md:hidden space-y-4">
            {recentReports.map((report) => (
              <div
                key={report.id}
                className="p-4 rounded-xl bg-[var(--bg-card)] border border-primary/10"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <p className="font-semibold text-[var(--text-primary)] mb-1">
                      {report.type}
                    </p>
                    <p className="text-xs text-[var(--text-primary)] opacity-70">
                      {report.dateGenerated}
                    </p>
                  </div>
                  <span className="text-xs text-[var(--text-primary)] opacity-50">
                    —
                  </span>
                </div>
                <div>
                  <span
                    className={`text-sm font-semibold ${
                      report.status === "Completed"
                        ? "bg-gradient-to-r from-[#22c55e] to-[#16a34a] bg-clip-text text-transparent"
                        : "text-yellow-400"
                    }`}
                  >
                    {report.status}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </Card>

        <div className="text-center py-6">
          <p className="text-sm text-[var(--text-primary)] opacity-60">
            © 2026 Aiarbix. All rights reserved.
          </p>
        </div>
      </div>
    </div>
  );
};

export default Reports;
